import type { Cliente, CanalTicket, NovoTicket, Ticket } from '@pgcustom/shared-types';
import { ApiError } from '../../core/api-client.js';
import { FORM_FIELD_CSS } from '../../core/form-field-styles.js';
import type { SuporteApi } from './suporte.api.js';

const ROTULO_CANAL: Record<CanalTicket, string> = {
  whatsapp: 'WhatsApp',
  email: 'E-mail',
  site: 'Site',
};

/**
 * `<erp-ticket-form-modal>` — abre um novo ticket de suporte.
 * `garantiaId` (opcional) é o que caracteriza "acionamento de garantia"
 * (`PRODUCT.md` 3.7) — preenchido a partir do id exibido em
 * Garantias, sem um seletor dedicado ainda (as duas telas não
 * compartilham estado entre si nesta sprint). Mesmo padrão de
 * `<erp-movimentacao-form-modal>`.
 */
export class ErpTicketFormModal extends HTMLElement {
  private readonly root: ShadowRoot;
  private _suporteApi: SuporteApi | null = null;
  private _aoSalvar: ((ticket: Ticket) => void) | null = null;

  private aberto = false;
  private clientes: Cliente[] = [];
  private enviando = false;
  private mensagemErro = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set suporteApi(valor: SuporteApi) {
    this._suporteApi = valor;
  }

  set aoSalvar(valor: (ticket: Ticket) => void) {
    this._aoSalvar = valor;
  }

  abrir(clientes: Cliente[]): void {
    this.clientes = clientes;
    this.mensagemErro = '';
    this.aberto = true;
    this.render();
  }

  connectedCallback(): void {
    this.render();
  }

  private fechar(): void {
    if (this.enviando) return;
    this.aberto = false;
    this.render();
  }

  private async aoSubmeter(evento: Event): Promise<void> {
    evento.preventDefault();
    if (!this._suporteApi || this.enviando) return;

    const clienteId = this.valorCampo('#cliente');
    const canal = this.valorCampo('#canal') as CanalTicket;
    const garantiaId = this.valorCampo('#garantia-id').trim();
    const assunto = this.valorCampo('#assunto').trim();
    const descricao = this.valorCampo('#descricao').trim();

    if (!clienteId) {
      this.mensagemErro = 'selecione um cliente';
      this.render();
      return;
    }
    if (!assunto) {
      this.mensagemErro = 'assunto é obrigatório';
      this.render();
      return;
    }
    if (!descricao) {
      this.mensagemErro = 'descrição é obrigatória';
      this.render();
      return;
    }

    this.enviando = true;
    this.mensagemErro = '';
    this.render();

    try {
      const dado: NovoTicket = {
        clienteId,
        canal,
        assunto,
        descricao,
        ...(garantiaId ? { garantiaId } : {}),
      };
      const ticket = await this._suporteApi.criar(dado);
      this.aberto = false;
      this._aoSalvar?.(ticket);
    } catch (erro) {
      this.mensagemErro = erro instanceof ApiError ? erro.message : 'não foi possível criar o ticket. tente novamente.';
    } finally {
      this.enviando = false;
      this.render();
    }
  }

  private valorCampo(seletor: string): string {
    return this.root.querySelector<HTMLInputElement | HTMLSelectElement>(seletor)?.value ?? '';
  }

  private render(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; }
        form { display: flex; flex-direction: column; gap: var(--pg-space-4, 16px); font-family: var(--pg-font-familia-base, system-ui); }
        ${FORM_FIELD_CSS}
        textarea { resize: vertical; }
        .rodape { display: flex; justify-content: flex-end; gap: var(--pg-space-3, 12px); }
      </style>
      <pg-modal id="modal" ${this.aberto ? 'open' : ''}>
        <span slot="header">Novo ticket</span>
        <form novalidate>
          <label>
            Cliente
            <select id="cliente">
              <option value="">Selecione…</option>
              ${this.clientes.map((c) => `<option value="${c.id}">${escaparHtml(c.nome)}</option>`).join('')}
            </select>
          </label>
          <label>
            Canal
            <select id="canal">
              ${Object.entries(ROTULO_CANAL)
                .map(([valor, rotulo]) => `<option value="${valor}">${rotulo}</option>`)
                .join('')}
            </select>
          </label>
          <label>Garantia acionada (opcional) <input id="garantia-id" type="text" placeholder="id da garantia, se for acionamento" /></label>
          <label>Assunto <input id="assunto" type="text" maxlength="200" /></label>
          <label>Descrição <textarea id="descricao" rows="4" maxlength="5000"></textarea></label>
          <span class="erro">${escaparHtml(this.mensagemErro)}</span>
        </form>
        <div slot="footer" class="rodape">
          <pg-button id="botao-cancelar" variant="ghost">Cancelar</pg-button>
          <pg-button id="botao-salvar" variant="primary" ${this.enviando ? 'loading' : ''}>Criar ticket</pg-button>
        </div>
      </pg-modal>
    `;

    const form = this.root.querySelector('form');
    form?.addEventListener('submit', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-salvar')?.addEventListener('pg-click', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-cancelar')?.addEventListener('pg-click', () => this.fechar());
    this.root.querySelector('#modal')?.addEventListener('pg-close', () => this.fechar());
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-ticket-form-modal', ErpTicketFormModal);

declare global {
  interface HTMLElementTagNameMap {
    'erp-ticket-form-modal': ErpTicketFormModal;
  }
}
